import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import type { Components } from "react-markdown";
import type { WebsiteIdea } from "../../content/ideas";

const PRIORITY_COLORS: Record<WebsiteIdea["priority"], string> = {
  high: "bg-rose-500/20 text-rose-400 border-rose-500/30",
  medium: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  low: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

const STATUS_COLORS: Record<WebsiteIdea["status"], string> = {
  draft: "bg-warm-500/20 text-warm-400 border-warm-500/30",
  active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  done: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  archived: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

const markdownComponents: Components = {
  code({ className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    const language = match ? match[1] : "";

    return match ? (
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={language}
        PreTag="div"
        className="rounded-lg !text-xs"
        customStyle={{ fontSize: "0.75rem", lineHeight: "1.25rem" }}
      >
        {String(children).replace(/\n$/, "")}
      </SyntaxHighlighter>
    ) : (
      <code className="inline-code" {...props}>
        {children}
      </code>
    );
  },
};

function stripH1(body: string): string {
  const lines = body.split("\n");
  if (lines.length > 0 && lines[0].startsWith("# ")) {
    return lines.slice(1).join("\n").trimStart();
  }
  return body;
}

interface IdeaCardProps {
  idea: WebsiteIdea;
}

export function IdeaCard({ idea }: IdeaCardProps) {
  const { t } = useTranslation();
  const bodyContent = stripH1(idea.body);

  function timeAgo(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return t('common.timeAgo.justNow');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t('common.timeAgo.minutesAgo', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('common.timeAgo.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    if (days < 30) return t('common.timeAgo.daysAgo', { count: days });
    const months = Math.floor(days / 30);
    if (months < 12) return t('common.timeAgo.monthsAgo', { count: months });
    const years = Math.floor(months / 12);
    return t('common.timeAgo.yearsAgo', { count: years });
  }

  return (
    <div className="bg-dark-card border border-dark-border rounded-lg p-6 overflow-hidden">
      <div className="flex items-start gap-2 mb-3">
        <h3 className="text-lg font-semibold text-warm-50 min-w-0 flex-1">{idea.title}</h3>
        <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${PRIORITY_COLORS[idea.priority]}`}
          >
            {idea.priority}
          </span>
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${STATUS_COLORS[idea.status]}`}
          >
            {idea.status}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {idea.tags.map((tag) => (
          <span key={tag} className="bg-dark-hover text-warm-400 text-xs px-2 py-0.5 rounded">
            {tag}
          </span>
        ))}
        <span className="text-xs text-warm-500">{t('future.created')} {timeAgo(idea.createdAt)}</span>
        <span className="text-xs text-warm-500">{t('future.updated')} {timeAgo(idea.updatedAt)}</span>
      </div>

      <div className="prose prose-invert max-w-none text-sm text-warm-200 [&_pre]:overflow-x-auto">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {bodyContent}
        </ReactMarkdown>
      </div>
    </div>
  );
}
